import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useTripStore = defineStore("trips", () => {
  const trips = ref([]);
  const meta = ref(null);
  const isLoading = ref(false);
  const isExporting = ref(false);

  const fetchTrips = async (parameters = {}) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const params = {
        page: 1,
        ...parameters,
      };

      const response = await axios.get("/api/trips", { params });
      trips.value = response.data.data;
      meta.value = response.data.meta;
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to fetch trips";
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTripById = async (id) => {
    isLoading.value = true;
    try {
      const response = await axios.get(`/api/trips/${id}`);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to get trip";
    } finally {
      isLoading.value = false;
    }
  };

  const addTrip = async (tripData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post("/api/trips", tripData);
      trips.value.push(response.data.data);
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to add trip";
    } finally {
      isLoading.value = false;
    }
  };

  const updateTrip = async (id, tripData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/trips/${id}`, tripData);
      const index = trips.value.findIndex((trip) => trip.id === id);
      if (index !== -1) {
        trips.value[index] = response.data.data;
      }
      return response.data.data;
    } catch (err) {
      throw err.response?.data?.message || "Failed to update trip";
    } finally {
      isLoading.value = false;
    }
  };

  const deleteTrip = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.delete(`/api/trips/${id}`);
      trips.value = trips.value.filter((trip) => trip.id !== id);
      return true;
    } catch (err) {
      throw err.response?.data?.message || "Failed to delete trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Register new trip
  const registerTrip = async (registrationData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post(
        "/api/trips/registration",
        registrationData,
      );
      // Optionally fetch the newly created trip to add it to the store
      if (response.data.trip_id) {
        const tripResponse = await fetchTripById(response.data.trip_id);
        trips.value.push(tripResponse);
      }
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        (err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(", ")
          : "Failed to register trip");
      throw errorMessage;
    } finally {
      isLoading.value = false;
    }
  };

  // New registration-related methods
  const registerToTravel = async (registrationData) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.post(
        "/api/trips/registration-to-travel",
        registrationData,
      );
      // Optionally fetch the newly created trip to add it to the store
      if (response.data.trip_id) {
        const tripResponse = await fetchTripById(response.data.trip_id);
        trips.value.push(tripResponse);
      }
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        (err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(", ")
          : "Failed to register trip");
      throw errorMessage;
    } finally {
      isLoading.value = false;
    }
  };

  // Cancel trip by defining travel id to null
  const cancelTrip = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.put(`/api/trips/${id}/cancel`, {
        travel_id: null,
      });
    } catch (err) {
      throw err.response?.data?.message || "Failed to cancel trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Confirm trip
  const confirmTrip = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      await axios.put(`/api/trips/${id}/confirm`);
    } catch (err) {
      throw err.response?.data?.message || "Failed to confirm trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Complete trip and update travel
  const completeTrip = async (id) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/trips/${id}/complete`);
      // Update the trip in the store with the response data
      const index = trips.value.findIndex((trip) => trip.id === id);
      if (index !== -1) {
        trips.value[index] = response.data.trip;
      }
      return response.data.trip;
    } catch (err) {
      throw err.response?.data?.message || "Failed to complete trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Complete trip and update travel
  const passengerNotArrived = async (id, driverMessage) => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const response = await axios.put(`/api/trips/${id}/client-not-arrived`, {
        driver_notice: driverMessage,
      });
      // Update the trip in the store with the response data
      const index = trips.value.findIndex((trip) => trip.id === id);
      if (index !== -1) {
        trips.value[index] = response.data.trip;
      }
      return response.data.trip;
    } catch (err) {
      throw err.response?.data?.message || "Failed to update trip";
    } finally {
      isLoading.value = false;
    }
  };

  // Export trips
  const exportTrips = async (parameters = {}) => {
    if (isExporting.value) return;
    isExporting.value = true;

    try {
      const response = await axios.get("/api/trips/export", {
        params: parameters,
        responseType: "blob",
      });

      // Define filename directly in the frontend
      const currentDate = new Date().toISOString().slice(0, 10);
      const fileName = `trips_export_${currentDate}.xlsx`;

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting trips:", err);
    } finally {
      isExporting.value = false;
    }
  };

  return {
    trips,
    meta,
    isLoading,
    fetchTrips,
    fetchTripById,
    addTrip,
    updateTrip,
    deleteTrip,
    registerTrip,
    registerToTravel,
    cancelTrip,
    confirmTrip,
    completeTrip,
    passengerNotArrived,
    isExporting,
    exportTrips,
  };
});
